import React, { createContext } from 'react';

interface ScannerEventContextProps {}

export interface ScannerEventContextData {
  addListener: (callback: (code: string) => void) => void;
  removeListener: (callback: (code: string) => void) => void;
}

interface ScannerEventContextState {}

const ScannerEventContext = createContext<ScannerEventContextData>({
  addListener: () => {},
  removeListener: () => {},
});

const MODIFIER_KEYS = ['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock', 'NumLock', 'ScrollLock'];

// Символ, который сканер реально «напечатал». Возвращает undefined для служебных клавиш
// (Enter, Tab и т.п.) — по ним код считается собранным.
const charFromKeyEvent = (e: any): string | undefined => {
  // e.key — то, что увидело бы обычное текстовое поле: верный регистр и верные знаки,
  // включая дефис в кодах вида "10-000628" (его старый разбор по keyCode превращал в "½"
  // и обрывал сбор кода посреди штрих-кода).
  if (typeof e.key === 'string' && e.key.length === 1 && e.key >= ' ' && e.key <= '~') {
    return e.key;
  }
  // Но при русской раскладке ОС в e.key приезжает кириллица, хотя сканер прислал латиницу,
  // поэтому для букв и цифр падаем на физическую клавишу — она от раскладки не зависит.
  const code: string = typeof e.code === 'string' ? e.code : '';
  if (/^Key[A-Z]$/.test(code)) {
    return code.slice(3);
  }
  if (/^(Digit|Numpad)[0-9]$/.test(code)) {
    return code.slice(-1);
  }
  return undefined;
};

class ScannerEventContextProvider extends React.Component<ScannerEventContextProps, ScannerEventContextState> {
  constructor(props: ScannerEventContextProps) {
    super(props);
    this.state = {
      lastEvents: [],
    };
  }

  listeners: ((code: string) => void)[] = [];
  lastEvents: Array<{ key: string; ts: number }> = [];
  timerId?: NodeJS.Timeout;

  addListener = (callback: (code: string) => void) => {
    // console.log('ScannerEventContextProvider::addListener');
    this.listeners.push(callback);
  };

  removeListener = (callback: (code: string) => void) => {
    // console.log('ScannerEventContextProvider::removeListener');
    const index = this.listeners.indexOf(callback);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  };

  checkCode = () => {
    if (this.lastEvents.length > 0) {
      const lastTs = this.lastEvents[this.lastEvents.length - 1].ts;
      this.lastEvents = this.lastEvents.filter(e => lastTs - e.ts < 1000);
    }
    if (this.lastEvents.length >= 6) {
      let code = this.lastEvents.reduce((a, b) => a + b.key, '');
      this.listeners.forEach(c => c(code));
      this.lastEvents = [];
      if (this.timerId) {
        clearTimeout(this.timerId);
      }
      return true;
    }
    return false;
  };

  handleKey = (e: any) => {
    // Роутер собирается с basename '/app' (см. App.jsx), поэтому реальный путь стойки —
    // '/app/reception'. Сверяем путь без базы, иначе перехватчик молчит везде, кроме дева
    // на корне, и сканер на стойках контроля доступа и саморегистрации не работает (EV-72).
    if (!window.location.pathname.replace(/^\/app(?=\/|$)/, '').startsWith('/reception')) {
      return;
    }
    if (MODIFIER_KEYS.indexOf(e.key) !== -1) {
      // Заглавные буквы сканер шлёт как Shift+клавиша, модификатор сам по себе код не обрывает.
      return;
    }

    // Суть такая - храним массив нажатий и их таймштампов. Записываем в него только печатные символы.
    // Проверяем наличие кода по непечатной клавише (Enter и т.п.) и по таймеру. Таймер ставим на 200мс
    // и ожидаем, что все нажатия должны укладываться в 1000мс
    const ts = new Date().getTime();
    const key = charFromKeyEvent(e);

    if (key !== undefined) {
      this.lastEvents = this.lastEvents.concat({ key, ts });
      if (this.timerId) {
        clearTimeout(this.timerId);
      }
      this.timerId = setTimeout(this.checkCode, 200);
    } else {
      if (this.checkCode()) {
        e.stopPropagation();
        e.preventDefault();
      }
    }
  };

  componentDidMount(): void {
    // console.log('ScannerEventContextProvider::mount');
    document.body.addEventListener('keydown', this.handleKey, { capture: true });
  }

  componentWillUnmount(): void {
    // console.log('ScannerEventContextProvider::unmount');
    document.body.removeEventListener('keydown', this.handleKey);
  }

  render() {
    // console.log('ScannerEventContextProvider::render');
    return (
      <ScannerEventContext.Provider
        value={{
          addListener: this.addListener,
          removeListener: this.removeListener,
        }}
      >
        {this.props.children}
      </ScannerEventContext.Provider>
    );
  }
}

export { ScannerEventContext, ScannerEventContextProvider };
